<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import type { ExtendedCharity } from '../../../../lib/api/employee/donation.service';
  import Button from '../../../Button.svelte';
  import Input from '../../../Input.svelte';
  import Textarea from '../../../Textarea.svelte';
  import { onDestroy } from 'svelte';
  import EmployeeDonationService from '../../../../lib/api/employee/donation.service';

  export let selectedCharity: ExtendedCharity | null = null;
  export let charities: ExtendedCharity[] = [];
  export let searchQuery = '';
  export let isLoadingCharities = false;
  export let loading = false;
  export let isReadOnly = false;

  const dispatch = createEventDispatcher();
  let searchTimeout: ReturnType<typeof setTimeout>;

  // Limit initial display
  const INITIAL_DISPLAY_LIMIT = 6;
  let showingAll = false;

  // Add charity modal state
  let showAddCharityModal = false;
  let addingCharity = false;
  let newCharityName = "";
  let newCharityEmail = "";
  let newCharityWebsite = "";
  let newCharityReason = "";
  let addCharityError = "";

  // Track charity name to auto-select after creation
  let charityNameToSelect: string | null = null;

  // Reactive statement to auto-select charity when it appears in the list
  $: if (charityNameToSelect && charities.length > 0) {
    const newCharity = charities.find(c => 
      c.name.toLowerCase() === charityNameToSelect!.toLowerCase()
    );
    if (newCharity) {
      handleSelectCharity(newCharity);
      charityNameToSelect = null; // Reset after selection
    }
  }

  // Show limited charities or all based on search/display state
  $: displayedCharities = showingAll || searchQuery ? charities : charities.slice(0, INITIAL_DISPLAY_LIMIT);
  $: hasMoreCharities = !searchQuery && charities.length > INITIAL_DISPLAY_LIMIT;

  function handleSearch(event: Event) {
    const target = event.target as HTMLInputElement;
    searchQuery = target.value;
    
    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set a new timeout for 1 second
    searchTimeout = setTimeout(() => {
      dispatch('search', { query: searchQuery });
    }, 1000);
  }

  // Clean up the timeout when the component is destroyed
  onDestroy(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
  });

  function handleSelectCharity(charity: ExtendedCharity) {
    if (isReadOnly) return; // Don't allow selection changes in read-only mode
    selectedCharity = charity;
    dispatch('charitySelected', { charity });
    // Go directly to review step when selecting existing charity
    setTimeout(() => {
      dispatch('goToReview');
    }, 300);
  }

  function handleNext() {
    if (isReadOnly && selectedCharity) {
      // In read-only mode, go directly to review
      dispatch('goToReview');
    } else {
      dispatch('next');
    }
  }

  function showMoreCharities() {
    showingAll = true;
  }

  function openAddCharityModal() {
    showAddCharityModal = true;
    newCharityName = "";
    newCharityEmail = "";
    newCharityWebsite = "";
    newCharityReason = "";
    addCharityError = "";
  }

  function closeAddCharityModal() {
    showAddCharityModal = false;
    newCharityName = "";
    newCharityEmail = "";
    newCharityWebsite = "";
    newCharityReason = "";
    addCharityError = "";
  }

  async function createCharity() {
    if (!newCharityName.trim() || !newCharityEmail.trim()) {
      addCharityError = "Charity name and email are required";
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newCharityEmail.trim())) {
      addCharityError = "Please enter a valid email address";
      return;
    }

    addingCharity = true;
    addCharityError = "";

    try {
      // Normalize website URL - add https:// if not present
      let normalizedWebsite = newCharityWebsite.trim();
      if (normalizedWebsite && !normalizedWebsite.startsWith('http://') && !normalizedWebsite.startsWith('https://')) {
        normalizedWebsite = `https://${normalizedWebsite}`;
      }
      
      const invitationData = {
        name: newCharityName.trim(),
        contact_email: newCharityEmail.trim(),
        website: normalizedWebsite || undefined,
        reason_for_request: newCharityReason.trim() || undefined
      };

      const result = await EmployeeDonationService.inviteCharity(invitationData);

      if (result.success) {
        // Store the name to select after refresh
        charityNameToSelect = newCharityName.trim();
        
        // Close modal first
        closeAddCharityModal();
        
        // Dispatch success message to parent
        dispatch("show-notification", {
          message: "An invitation has been sent to your charity, we will notify you when they are onboard.",
          type: "success"
        });
        
        // Trigger refresh - the reactive statement will handle selection
        dispatch("refresh-charities");
      } else {
        // Handle API errors
        let errorMsg = result.message || "Failed to create charity invitation";
        if (result.errors && typeof result.errors === "object") {
          if (result.errors.errors) {
            const firstError = Object.values(result.errors.errors)[0];
            if (Array.isArray(firstError) && firstError.length > 0) {
              errorMsg = firstError[0];
            }
          }
        }
        addCharityError = errorMsg;
      }
    } catch (err: any) {
      addCharityError = err.message || "An unexpected error occurred";
    } finally {
      addingCharity = false;
    }
  }

  function handleBack() {
    dispatch('back');
  }

  function handleCancel() {
    dispatch('cancel');
  }

  // Action buttons - show Next button in read-only mode
  $: showNextButton = isReadOnly && selectedCharity;
</script>

<div class="step-container">
  <!-- Header -->
  <div class="step-header">
    <button class="back-button" on:click={handleBack} disabled={loading}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M19 12H6m6-6l-6 6 6 6"/>
      </svg>
    </button>
    <div class="header-content">
      <div class="header-indicator"></div>
      <h2 class="step-title">{isReadOnly ? 'Selected Charity' : 'Select a Charity'}</h2>
      <p class="step-subtitle">{isReadOnly ? 'The charity for this donation has been pre-selected' : 'Choose where to request your leave hours for donation'}</p>
    </div>
    <div class="header-spacer"></div>
  </div>

  <!-- Read-only mode: Show only selected charity -->
  {#if isReadOnly && selectedCharity}
    <div class="read-only-charity-section">
      <div class="read-only-notice">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <path d="M9 12l2 2 4-4"/>
        </svg>
        <span>Charity pre-selected for this donation</span>
      </div>
      <div class="charity-list">
        <div class="charity-card read-only-charity-card">
          <!-- Charity Info -->
          <div class="charity-info">
            <div class="charity-logo">
              {#if selectedCharity.logo}
                <img src={selectedCharity.logo} alt="{selectedCharity.name} logo" />
              {:else}
                <div class="charity-logo-placeholder">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                  </svg>
                </div>
              {/if}
            </div>
            <div class="charity-details">
              <div class="charity-header">
                <h3 class="charity-name">{selectedCharity.name}</h3>
                {#if selectedCharity.verified}
                  <div class="verified-badge">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="20,6 9,17 4,12"/>
                    </svg>
                  </div>
                {/if}
              </div>
              <p class="charity-description">{selectedCharity.description || `${selectedCharity.name} - Verified charity providing community support`}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  {:else}
    <!-- Search Bar -->
    <div class="search-container">
      <div class="search-input-wrapper">
        <input
          type="text"
          placeholder="Search charities"
          bind:value={searchQuery}
          on:input={handleSearch}
          class="search-input"
        />
        <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
      </div>
    </div>

    <!-- Charity List -->
    <div class="charity-list">
      {#if isLoadingCharities}
        <div class="loading-state">
          <div class="loading-spinner"></div>
          <p>Loading charities...</p>
        </div>
      {:else if displayedCharities.length === 0}
        <div class="empty-state">
          <p>No charities found</p>
          {#if searchQuery}
            <p class="empty-subtitle">Try adjusting your search or add a new charity</p>
          {/if}
        </div>
      {:else}
        {#each displayedCharities as charity (charity.uuid)}
          <div class="charity-card">
            <!-- Charity Info -->
            <div class="charity-info">
              <div class="charity-logo">
                {#if charity.logo}
                  <img src={charity.logo} alt="{charity.name} logo" />
                {:else}
                  <div class="charity-logo-placeholder">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                    </svg>
                  </div>
                {/if}
              </div>
              <div class="charity-details">
                <div class="charity-header">
                  <h3 class="charity-name">{charity.name}</h3>
                  {#if charity.verified}
                    <div class="verified-badge">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20,6 9,17 4,12"/>
                      </svg>
                    </div>
                  {/if}
                </div>
                <p class="charity-description">{charity.description || `${charity.name} - Verified charity providing community support`}</p>
              </div>
            </div>
            
            <!-- Select Button -->
            <Button 
              type="button" 
              variant="secondary"
              fullWidth={true}
              on:click={() => handleSelectCharity(charity)}
              disabled={loading}
            >
              Select Charity
            </Button>
          </div>
        {/each}
        
        {#if hasMoreCharities && !showingAll}
          <div class="show-more-section">
            <button class="show-more-button" on:click={showMoreCharities} disabled={loading}>
              Show {charities.length - INITIAL_DISPLAY_LIMIT} more charities
            </button>
          </div>
        {/if}
      {/if}
    </div>

    <!-- Add Charity Button -->
    <div class="invite-section">
      <Button 
        type="button" 
        variant="primary"
        fullWidth={true}
        on:click={openAddCharityModal}
        disabled={loading}
      >
        <span class="button-content">
          <svg 
            width="20" 
            height="20" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2"
            class="plus-icon"
            aria-hidden="true"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          <span class="button-text">Add New Charity</span>
        </span>
      </Button>
    </div>
  {/if}

  <!-- Action Buttons for Read-Only Mode -->
  {#if showNextButton}
    <div class="step-actions">
      <Button
        variant="primary"
        fullWidth={true}
        on:click={handleNext}
        disabled={loading || !selectedCharity}
      >
        {loading ? "Processing..." : "Continue to Review"}
      </Button>
      <Button
        variant="secondary"
        fullWidth={true}
        on:click={handleBack}
        disabled={loading}
      >
        Back
      </Button>
    </div>
  {/if}
</div>

<!-- Add Charity Modal -->
{#if showAddCharityModal}
  <div 
    class="modal-overlay" 
    on:click|self={closeAddCharityModal}
    role="presentation"
  >
    <div class="modal-container" role="dialog" aria-modal="true" aria-labelledby="add-charity-title">
      <div class="modal-content">
        <div class="modal-header">
          <h2 id="add-charity-title" class="modal-title">Add New Charity</h2>
          <button class="modal-close" on:click={closeAddCharityModal} disabled={addingCharity}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          {#if addCharityError}
            <div class="error-message" role="alert">{addCharityError}</div>
          {/if}

          <div class="form-field">
            <Input
              label="charity-name"
              placeholder="Enter charity name"
              bind:value={newCharityName}
              fullWidth={true}
              disabled={addingCharity}
              required={true}
            />
          </div>

          <div class="form-field">
            <Input
              label="charity-email"
              type="email"
              placeholder="Enter contact email"
              bind:value={newCharityEmail}
              fullWidth={true}
              disabled={addingCharity}
              required={true}
            />
          </div>

          <div class="form-field">
            <Input
              label="charity-website"
              placeholder="https://www.charity.org (optional)"
              bind:value={newCharityWebsite}
              fullWidth={true}
              disabled={addingCharity}
            />
          </div>

          <div class="form-field">
            <Textarea
              label="reason"
              placeholder="Why would you like this charity to be added? (optional)"
              bind:value={newCharityReason}
              fullWidth={true}
              disabled={addingCharity}
              rows={3}
            />
          </div>
        </div>

        <div class="modal-actions">
          <Button 
            variant="primary" 
            fullWidth={true}
            on:click={createCharity}
            disabled={addingCharity || !newCharityName.trim() || !newCharityEmail.trim()}
          >
            {addingCharity ? "Creating..." : "Create & Select"}
          </Button>
          <Button 
            variant="secondary" 
            fullWidth={true}
            on:click={closeAddCharityModal}
            disabled={addingCharity}
          >
            Cancel
          </Button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .step-container {
    padding: 1.5rem;
    max-height: 80vh;
    overflow-y: auto;
    background-color: #f9fafb;
  }

  .step-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 2rem;
  }

  .back-button {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 50%;
    color: #6b7280;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 0.5rem;
  }

  .back-button:hover:not(:disabled) {
    background-color: #f3f4f6;
    color: #374151;
  }

  .back-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .header-content {
    flex: 1;
    text-align: center;
    margin: 0 1rem;
  }

  .header-spacer {
    width: 40px; /* Same width as back button for centering */
  }

  .header-indicator {
    width: 50px;
    height: 4px;
    background-color: #e5e7eb;
    border-radius: 2px;
    margin: 0 auto 1.5rem;
  }

  .step-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
  }

  .search-container {
    margin-bottom: 1.5rem;
  }

  .search-input-wrapper {
    position: relative;
  }

  .search-input {
    width: 100%;
    padding: 0.875rem 3rem 0.875rem 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    font-size: 1rem;
    background-color: #f9fafb;
    transition: all 0.2s ease;
  }

  .search-input:focus {
    outline: none;
    border-color: #f59e0b;
    background-color: white;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.1);
  }

  .search-input::placeholder {
    color: #9ca3af;
  }

  .search-icon {
    position: absolute;
    right: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    pointer-events: none;
  }

  .charity-list {
    margin-bottom: 1.5rem;
  }

  .charity-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 16px;
    padding: 1.25rem;
    margin-bottom: 1rem;
    transition: all 0.2s ease;
  }

  .charity-card:hover {
    border-color: #d1d5db;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .charity-info {
    display: flex;
    gap: 1rem;
    margin-bottom: 1rem;
  }

  .charity-logo {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;
    background-color: #f3f4f6;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .charity-logo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .charity-logo-placeholder {
    color: #6b7280;
  }

  .charity-details {
    flex: 1;
    min-width: 0;
  }

  .charity-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.25rem;
  }

  .charity-name {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
    line-height: 1.4;
  }

  .verified-badge {
    width: 20px;
    height: 20px;
    background-color: #10b981;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .verified-badge svg {
    color: white;
    width: 12px;
    height: 12px;
  }

  .charity-description {
    color: #6b7280;
    font-size: 0.875rem;
    line-height: 1.4;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .invite-section {
    padding-top: 1rem;
    border-top: 1px solid #e5e7eb;
  }

  /* Read-only mode styles */
  .read-only-charity-section {
    margin-bottom: 1.5rem;
  }

  .read-only-notice {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background-color: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 8px;
    margin-bottom: 1rem;
    color: #1e40af;
    font-size: 0.875rem;
    font-weight: 500;
  }

  .read-only-notice svg {
    flex-shrink: 0;
    color: #3b82f6;
  }

  .read-only-charity-card {
    pointer-events: none;
    opacity: 1;
    border: 2px solid #3b82f6 !important;
    background-color: #f8fafc !important;
    cursor: default;
  }

  .read-only-charity-card:hover {
    transform: none;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  }

  .step-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
  }

  .loading-state,
  .empty-state {
    text-align: center;
    padding: 3rem 1rem;
    color: #6b7280;
  }

  .loading-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #f59e0b;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1rem;
  }

  .empty-subtitle {
    font-size: 0.875rem;
    margin-top: 0.5rem;
    opacity: 0.8;
  }

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .step-container {
      padding: 1rem;
    }

    .charity-info {
      gap: 0.75rem;
    }

    .charity-logo {
      width: 40px;
      height: 40px;
    }

    .charity-name {
      font-size: 1rem;
    }

    .charity-description {
      font-size: 0.8125rem;
    }
  }

  .button-content {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    line-height: 1;
    vertical-align: middle;
  }

  .plus-icon {
    flex-shrink: 0;
    display: inline-block;
    vertical-align: middle;
    position: relative;
    top: -0.0625em;
  }

  .button-text {
    display: inline-block;
    vertical-align: middle;
  }

  .show-more-section {
    margin-top: 1rem;
    text-align: center;
  }

  .show-more-button {
    background: none;
    border: none;
    color: var(--color-primary);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    padding: 0.5rem 1rem;
    border-radius: 8px;
    transition: all 0.2s ease;
  }

  .show-more-button:hover:not(:disabled) {
    background-color: #fffbf0;
  }

  .show-more-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Modal Styles */
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
  }

  .modal-container {
    background-color: white;
    width: 100%;
    max-width: 480px;
    border-radius: 12px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    overflow: hidden;
    border: 1px solid #e5e7eb;
    margin: 1rem;
  }

  .modal-content {
    padding: 2rem;
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1.5rem;
  }

  .modal-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0;
  }

  .modal-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.5rem;
    color: #6b7280;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .modal-close:hover:not(:disabled) {
    background-color: #f3f4f6;
    color: #374151;
  }

  .modal-close:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .modal-body {
    margin-bottom: 1.5rem;
  }

  .form-field {
    margin-bottom: 1.5rem;
  }

  .form-field:last-child {
    margin-bottom: 0;
  }

  .error-message {
    font-size: 0.75rem;
    color: #ef4444;
    margin: 0 0 1rem 0;
    display: flex;
    align-items: center;
    gap: 0.375rem;
  }

  .error-message::before {
    content: "⚠";
    font-size: 0.875rem;
  }

  .modal-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  /* Responsive design */
  @media (max-width: 768px) {
    .modal-container {
      margin: 0.5rem;
    }

    .modal-content {
      padding: 1.5rem;
    }
  }
</style> 